import React, { useState, useEffect } from 'react';
import { Sun, Moon, Clock, AlertTriangle, Star, Calendar } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DailyPanchang() {
  const [panchang, setPanchang] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLang();

  useEffect(() => {
    const fetchPanchang = () => {
      fetch(`${API_URL}/api/panchang`)
        .then(res => res.json())
        .then(data => { setPanchang(data); setLoading(false); })
        .catch(() => setLoading(false));
    };
    fetchPanchang();
    // Auto-refresh every 5 minutes for real-time tithi/nakshatra changes
    const interval = setInterval(fetchPanchang, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <section id="panchang" className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 cosmic-bg" />
        <div className="container-custom relative z-10 text-center">
          <div className="animate-pulse text-gold text-lg">{t('panchang.loading')}</div>
        </div>
      </section>
    );
  }

  if (!panchang) return null;

  const today = new Date();
  const dateStr = today.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <section id="panchang" className="section-padding relative overflow-hidden" data-testid="panchang-section">
      <div className="absolute inset-0 cosmic-bg" />
      <div className="absolute top-1/4 right-0 w-80 h-80 bg-gold/6 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-purple/8 rounded-full blur-[100px]" />

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-4">
            {t('panchang.tag')}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mb-4 tracking-tight">
            {t('panchang.title1')} <span className="text-gold-gradient">{t('panchang.title2')}</span>
          </h2>
          <p className="text-text-muted text-sm">{dateStr}</p>
          {panchang.last_updated && (
            <p className="text-gold/40 text-xs mt-1">
              {lang === 'hi' ? `अपडेट: ${panchang.last_updated} IST` : `Updated: ${panchang.last_updated} IST`}
            </p>
          )}
          <p className="text-gold/50 text-xs mt-1 font-heading italic">
            {panchang.paksha} | {lang === 'hi' ? 'सूर्य राशि' : 'Sun Sign'}: {panchang.sun_rashi} | {lang === 'hi' ? 'चंद्र राशि' : 'Moon Sign'}: {panchang.moon_rashi}
          </p>
          <div className="divine-divider mt-6 max-w-xs mx-auto" />
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Panchang Details */}
          <div className="glass-card p-6 md:p-8" data-testid="panchang-details">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center border border-gold/15">
                <Calendar className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h3 className="font-heading text-xl text-text-main">{t('panchang.details')}</h3>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { label: t('panchang.day'), value: panchang.day, icon: Sun, color: 'text-amber-400' },
                { label: t('panchang.tithi'), value: `${panchang.tithi} (${panchang.paksha})${panchang.tithi_end_time ? ` — ${panchang.tithi_end_time} ${t('panchang.until')}` : ''}`, icon: Moon, color: 'text-blue-300' },
                { label: t('panchang.nakshatra'), value: `${panchang.nakshatra}${panchang.nakshatra_end_time ? ` — ${panchang.nakshatra_end_time} ${t('panchang.until')}` : ''}`, icon: Star, color: 'text-purple-light' },
                { label: t('panchang.yoga'), value: panchang.yoga, icon: Star, color: 'text-gold' },
                { label: t('panchang.karana'), value: panchang.karana, icon: Star, color: 'text-emerald-400' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0" data-testid={`panchang-${i}`}>
                  <div className="flex items-center gap-3">
                    <item.icon className={`w-4 h-4 ${item.color}`} />
                    <span className="text-text-muted text-sm">{item.label}</span>
                  </div>
                  <span className="text-text-main font-medium text-sm">{item.value}</span>
                </div>
              ))}

              {/* Next Tithi & Nakshatra Info */}
              {panchang.next_tithi && panchang.tithi_end_time && (
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/5 rounded-xl p-3 border border-blue-500/10 mt-2" data-testid="next-tithi-info">
                  <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{t('panchang.nextTithi')}</p>
                  <p className="text-text-main font-medium text-sm">
                    {panchang.tithi_end_time} {t('panchang.after')} → <span className="text-gold">{panchang.next_tithi}</span> ({panchang.next_paksha})
                  </p>
                </div>
              )}
              {panchang.next_nakshatra && panchang.nakshatra_end_time && (
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/5 rounded-xl p-3 border border-purple-500/10 mt-2" data-testid="next-nakshatra-info">
                  <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{lang === 'hi' ? 'अगला नक्षत्र' : 'Next Nakshatra'}</p>
                  <p className="text-text-main font-medium text-sm">
                    {panchang.nakshatra_end_time} {t('panchang.after')} → <span className="text-purple-light">{panchang.next_nakshatra}</span>
                  </p>
                </div>
              )}

              {/* Sunrise / Sunset / Moonrise / Moonset */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-xl p-3 text-center border border-amber-500/10">
                  <Sun className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                  <p className="text-[10px] text-text-muted uppercase tracking-wider">{t('panchang.sunrise')}</p>
                  <p className="text-text-main font-heading text-base font-medium" data-testid="sunrise-time">{panchang.sunrise}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/5 rounded-xl p-3 text-center border border-orange-500/10">
                  <Sun className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                  <p className="text-[10px] text-text-muted uppercase tracking-wider">{t('panchang.sunset')}</p>
                  <p className="text-text-main font-heading text-base font-medium" data-testid="sunset-time">{panchang.sunset}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/5 rounded-xl p-3 text-center border border-blue-500/10">
                  <Moon className="w-4 h-4 text-blue-300 mx-auto mb-1" />
                  <p className="text-[10px] text-text-muted uppercase tracking-wider">{t('panchang.moonrise')}</p>
                  <p className="text-text-main font-heading text-base font-medium" data-testid="moonrise-time">{panchang.moonrise}</p>
                </div>
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/5 rounded-xl p-3 text-center border border-indigo-500/10">
                  <Moon className="w-4 h-4 text-indigo-300 mx-auto mb-1" />
                  <p className="text-[10px] text-text-muted uppercase tracking-wider">{t('panchang.moonset')}</p>
                  <p className="text-text-main font-heading text-base font-medium" data-testid="moonset-time">{panchang.moonset || '--:--'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rahukaal & Inauspicious Times */}
          <div className="space-y-6">
            {/* Rahukaal */}
            <div className="glass-card p-6 md:p-8 border-red-500/10" data-testid="rahukaal-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-red-600/5 flex items-center justify-center border border-red-500/15">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-heading text-xl text-text-main">{t('panchang.rahukaal')}</h3>
                  <p className="text-text-muted text-xs">{t('panchang.rahukaalDesc')}</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-500/10 to-red-600/5 rounded-xl p-5 border border-red-500/15 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-300 text-xs uppercase tracking-wider mb-1">{t('panchang.todayRahukaal')}</p>
                    <p className="text-text-main font-heading text-2xl font-medium" data-testid="rahukaal-time">
                      {panchang.rahukaal.start} - {panchang.rahukaal.end}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-red-400/40" />
                </div>
                <p className="text-red-300/60 text-xs mt-2">{t('panchang.noAuspicious')}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/3 rounded-xl p-4 border border-white/5">
                  <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{t('panchang.yamaganda')}</p>
                  <p className="text-text-main font-medium text-sm" data-testid="yamaganda-time">
                    {typeof panchang.yamaganda === 'string'
                      ? panchang.yamaganda
                      : (panchang.yamaganda?.start
                          ? `${panchang.yamaganda.start} - ${panchang.yamaganda.end}`
                          : (panchang.yamaganda_obj?.start
                              ? `${panchang.yamaganda_obj.start} - ${panchang.yamaganda_obj.end}`
                              : '—'))}
                  </p>
                </div>
                <div className="bg-white/3 rounded-xl p-4 border border-white/5">
                  <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{t('panchang.gulika')}</p>
                  <p className="text-text-main font-medium text-sm" data-testid="gulika-time">
                    {typeof panchang.gulika === 'string'
                      ? panchang.gulika
                      : (panchang.gulika?.start
                          ? `${panchang.gulika.start} - ${panchang.gulika.end}`
                          : (panchang.gulika_obj?.start
                              ? `${panchang.gulika_obj.start} - ${panchang.gulika_obj.end}`
                              : '—'))}
                  </p>
                </div>
              </div>
            </div>

            {/* Shubh Muhurat Info */}
            <div className="glass-card p-6 border-gold/10" data-testid="muhurat-info">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold/15 to-gold/5 flex items-center justify-center border border-gold/10">
                  <Star className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h4 className="font-heading text-lg text-text-main">{t('panchang.muhurat')}</h4>
                </div>
              </div>
              <p className="text-text-muted text-sm leading-relaxed">
                {t('panchang.muhuratDesc')}
              </p>
              <a href="#book-consultation" className="btn-gold-outline text-sm mt-4 inline-flex items-center gap-2">
                {t('panchang.muhuratCta')}
              </a>
            </div>
          </div>
        </div>

        {/* CTA to full panchang page */}
        <div className="text-center mt-10">
          <a href="/daily-panchang" className="btn-gold inline-flex items-center gap-2" data-testid="full-panchang-link">
            {lang === 'hi' ? 'पूरा पंचांग विस्तार से देखें' : 'View Full Panchang Details'}
          </a>
        </div>
      </div>
    </section>
  );
}
